import fs from 'fs-extra';
import path from 'path';
import plist from 'plist';
import { parseString, Builder } from 'xml2js';
import { ImageProcessor } from '../utils/image';
import { SplashConfig } from '../types/config';
import { IOSTemplates } from '../templates/ios-templates';
import { FlavorHelper } from '../core/flavor-helper';

export class IOSPlatform {
  constructor(private flavorHelper: FlavorHelper) {}

  async generateSplash(config: SplashConfig): Promise<void> {
    console.log('[iOS] Generating splash screen...');

    // Generate images
    await this.generateImages(config);

    // Generate background images
    await this.generateBackgrounds(config);

    // Create/update storyboard
    await this.updateLaunchScreenStoryboard(config);

    // Update Info.plist files
    await this.updateInfoPlistFiles(config);

    console.log('[iOS] Splash screen generation complete!');
  }

  private async generateImages(config: SplashConfig): Promise<void> {
    const imagePath = config.imageIos || config.image;
    const darkImagePath = config.darkImageIos || config.darkImage;
    const brandingPath = config.brandingIos || config.branding;
    const brandingDarkPath = config.brandingDarkIos || config.brandingDark;

    // Main splash images
    if (imagePath) {
      await this.generateImageSet(
        imagePath,
        this.flavorHelper.iOSAssetsLaunchImageFolder,
        'LaunchImage',
        false
      );
    } else {
      // Create 1x1 transparent images
      await this.createTransparentImages(
        this.flavorHelper.iOSAssetsLaunchImageFolder,
        'LaunchImage'
      );
    }

    // Dark splash images
    if (darkImagePath) {
      await this.generateImageSet(
        darkImagePath,
        this.flavorHelper.iOSAssetsLaunchImageFolder,
        'LaunchImage',
        true
      );
    }

    // Branding images
    if (brandingPath) {
      await this.generateImageSet(
        brandingPath,
        this.flavorHelper.iOSAssetsBrandingImageFolder,
        'BrandingImage',
        false
      );
    }

    if (brandingDarkPath) {
      await this.generateImageSet(
        brandingDarkPath,
        this.flavorHelper.iOSAssetsBrandingImageFolder,
        'BrandingImage',
        true
      );
    }

    // Create Contents.json files
    await this.createContentsJson(config);
  }

  private async generateImageSet(
    imagePath: string,
    outputFolder: string,
    baseName: string,
    dark = false
  ): Promise<void> {
    const templates = ImageProcessor.getIOSTemplates(baseName, dark);

    await ImageProcessor.processImage(imagePath, templates, outputFolder);
  }

  private async createTransparentImages(outputFolder: string, baseName: string): Promise<void> {
    await fs.ensureDir(outputFolder);

    const templates = ImageProcessor.getIOSTemplates(baseName, false);

    for (const template of templates) {
      await ImageProcessor.createSolidColorImage(
        'ffffff',
        1,
        1,
        path.join(outputFolder, template.fileName)
      );
    }
  }

  private async createContentsJson(config: SplashConfig): Promise<void> {
    const launchImageFolder = this.flavorHelper.iOSAssetsLaunchImageFolder;
    await fs.ensureDir(launchImageFolder);

    const hasDarkImage = !!(config.darkImageIos || config.darkImage);
    const contentsJson = hasDarkImage ? IOSTemplates.contentsJsonDark : IOSTemplates.contentsJson;

    await fs.writeFile(path.join(launchImageFolder, 'Contents.json'), contentsJson);

    // Branding Contents.json
    const brandingPath = config.brandingIos || config.branding;
    if (brandingPath) {
      const brandingFolder = this.flavorHelper.iOSAssetsBrandingImageFolder;
      await fs.ensureDir(brandingFolder);

      const hasDarkBranding = !!(config.brandingDarkIos || config.brandingDark);
      const brandingContentsJson = hasDarkBranding
        ? IOSTemplates.brandingContentsJsonDark
        : IOSTemplates.brandingContentsJson;

      await fs.writeFile(path.join(brandingFolder, 'Contents.json'), brandingContentsJson);
    }
  }

  private async generateBackgrounds(config: SplashConfig): Promise<void> {
    const color = config.colorIos || config.color;
    const darkColor = config.darkColorIos || config.darkColor;
    const backgroundImage = config.backgroundImageIos || config.backgroundImage;
    const darkBackgroundImage = config.darkBackgroundImageIos || config.darkBackgroundImage;

    const backgroundFolder = this.flavorHelper.iOSAssetsLaunchBackgroundFolder;
    await fs.ensureDir(backgroundFolder);

    // Light background
    if (backgroundImage) {
      await ImageProcessor.copyAndConvertImage(
        backgroundImage,
        path.join(backgroundFolder, 'background.png'),
        'png'
      );
    } else if (color) {
      await ImageProcessor.createSolidColorImage(
        color,
        1,
        1,
        path.join(backgroundFolder, 'background.png')
      );
    }

    // Dark background
    if (darkBackgroundImage) {
      await ImageProcessor.copyAndConvertImage(
        darkBackgroundImage,
        path.join(backgroundFolder, 'darkbackground.png'),
        'png'
      );
    } else if (darkColor) {
      await ImageProcessor.createSolidColorImage(
        darkColor,
        1,
        1,
        path.join(backgroundFolder, 'darkbackground.png')
      );
    }

    // Create background Contents.json
    const hasDarkBackground = !!(darkColor || darkBackgroundImage);
    const backgroundContentsJson = hasDarkBackground
      ? IOSTemplates.launchBackgroundDarkJson
      : IOSTemplates.launchBackgroundJson;

    await fs.writeFile(path.join(backgroundFolder, 'Contents.json'), backgroundContentsJson);
  }

  private async updateLaunchScreenStoryboard(config: SplashConfig): Promise<void> {
    const storyboardPath = this.flavorHelper.iOSLaunchScreenStoryboardFile;
    console.log(`[iOS] Updating ${storyboardPath}`);

    await fs.ensureDir(path.dirname(storyboardPath));

    const imagePath = config.imageIos || config.image;
    const brandingPath = config.brandingIos || config.branding;
    const contentMode = config.iosContentMode || 'center';
    const brandingContentMode = config.iosBrandingContentMode || 'bottom';
    const brandingPadding = config.brandingBottomPaddingIos || config.brandingBottomPadding || 0;

    // Create or update storyboard
    if (!(await fs.pathExists(storyboardPath))) {
      const storyboardContent = IOSTemplates.launchScreenStoryboard.replace(
        /\[LAUNCH_IMAGE_PLACEHOLDER\]/g,
        this.flavorHelper.iOSLaunchImageName
      );

      await fs.writeFile(storyboardPath, storyboardContent);
    }

    // Update existing storyboard
    await this.updateStoryboardContent(
      storyboardPath,
      imagePath,
      brandingPath,
      contentMode,
      brandingContentMode,
      brandingPadding
    );
  }

  private async updateStoryboardContent(
    storyboardPath: string,
    imagePath?: string,
    brandingPath?: string,
    contentMode = 'center',
    brandingContentMode = 'bottom',
    brandingPadding = 0
  ): Promise<void> {
    const content = await fs.readFile(storyboardPath, 'utf-8');

    parseString(content, async (err, result) => {
      if (err) {
        console.error('Error parsing storyboard:', err);
        return;
      }

      // Find the main view
      const document = result.document;
      const scenes = document.scenes[0].scene;
      const viewController = scenes[0].objects[0].viewController[0];
      const view = viewController.view[0];

      // Update content mode for main image
      if (view.subviews && view.subviews[0].imageView) {
        const imageViews = view.subviews[0].imageView;
        const launchImageView = imageViews.find(
          (iv: any) => iv.$.image === this.flavorHelper.iOSLaunchImageName
        );

        if (launchImageView) {
          launchImageView.$.contentMode = contentMode;
        }

        // Handle branding image
        if (brandingPath && brandingContentMode !== contentMode) {
          const brandingImageView = imageViews.find(
            (iv: any) => iv.$.image === this.flavorHelper.iOSBrandingImageName
          );

          if (!brandingImageView) {
            // Add branding image view
            const brandingSubview = IOSTemplates.brandingSubview.replace(
              '[BRANDING_IMAGE_PLACEHOLDER]',
              this.flavorHelper.iOSBrandingImageName
            );

            // Parse and add to subviews
            parseString(`<root>${brandingSubview}</root>`, (err, brandingResult) => {
              if (!err && brandingResult.root.imageView) {
                imageViews.push(brandingResult.root.imageView[0]);
              }
            });
          }

          if (brandingImageView) {
            brandingImageView.$.contentMode = brandingContentMode;
          }
        }
      }

      // Update constraints for branding
      if (brandingPath && view.constraints) {
        let constraintsXml = IOSTemplates.brandingCenterBottomConstraints;

        if (brandingContentMode === 'bottomLeft') {
          constraintsXml = IOSTemplates.brandingLeftBottomConstraints;
        } else if (brandingContentMode === 'bottomRight') {
          constraintsXml = IOSTemplates.brandingRightBottomConstraints;
        }

        constraintsXml = constraintsXml.replace('{bottom_padding}', brandingPadding.toString());

        // Parse and add constraints
        parseString(`<root>${constraintsXml}</root>`, (err, constraintsResult) => {
          if (!err && constraintsResult.root.constraints) {
            if (!view.constraints[0].constraint) {
              view.constraints[0].constraint = [];
            }
            view.constraints[0].constraint.push(
              ...constraintsResult.root.constraints[0].constraint
            );
          }
        });
      }

      // Add background constraints
      if (!view.constraints || !view.constraints[0].constraint) {
        parseString(`<root>${IOSTemplates.launchBackgroundConstraints}</root>`, (err, bgResult) => {
          if (!err && bgResult.root.constraints) {
            view.constraints = bgResult.root.constraints;
          }
        });
      }

      // Update image dimensions in resources
      if (imagePath && document.resources && document.resources[0].image) {
        const { width, height } = await ImageProcessor.getImageDimensions(imagePath);
        const launchImageResource = document.resources[0].image.find(
          (img: any) => img.$.name === this.flavorHelper.iOSLaunchImageName
        );

        if (launchImageResource) {
          launchImageResource.$.width = width.toString();
          launchImageResource.$.height = height.toString();
        }
      }

      // Convert back to XML
      const builder = new Builder({
        headless: true,
        renderOpts: { pretty: true, indent: '    ' },
      });
      const xml = builder.buildObject(result);
      await fs.writeFile(
        storyboardPath,
        `<?xml version="1.0" encoding="UTF-8" standalone="no"?>\n${xml}\n`
      );
    });
  }

  private async updateInfoPlistFiles(config: SplashConfig): Promise<void> {
    const plistFiles = config.infoPlistFiles || [this.flavorHelper.iOSInfoPlistFile];
    const fullscreen = config.fullscreen || false;

    for (const plistFile of plistFiles) {
      if (!(await fs.pathExists(plistFile))) {
        console.warn(`[iOS] Info.plist file not found: ${plistFile}`);
        continue;
      }

      console.log(`[iOS] Updating ${plistFile}`);
      await this.updateInfoPlist(plistFile, fullscreen);
    }
  }

  private async updateInfoPlist(plistFile: string, fullscreen: boolean): Promise<void> {
    const content = await fs.readFile(plistFile, 'utf-8');
    const plistData = plist.parse(content) as any;

    // Update status bar hidden
    plistData.UIStatusBarHidden = fullscreen;

    if (fullscreen) {
      plistData.UIViewControllerBasedStatusBarAppearance = false;
    }

    // Convert back to plist format
    const updatedContent = plist.build(plistData);
    await fs.writeFile(plistFile, updatedContent);
  }

  async removeSplash(): Promise<void> {
    console.log('[iOS] Removing splash screen...');

    // Remove asset folders
    const foldersToRemove = [
      this.flavorHelper.iOSAssetsLaunchImageFolder,
      this.flavorHelper.iOSAssetsBrandingImageFolder,
      this.flavorHelper.iOSAssetsLaunchBackgroundFolder,
    ];

    for (const folder of foldersToRemove) {
      if (await fs.pathExists(folder)) {
        await fs.remove(folder);
      }
    }

    console.log('[iOS] Splash screen removal complete!');
  }
}
